import type {SeriesScoreDto} from '@devmil/contracts';
import {useId} from 'react';

import {
  describeSourcedMetric,
  formatMetricValue,
  formatSample,
} from '../../lib/format.js';

/**
 * Desempenho por série: alcance médio por peça, na idade de um dia.
 *
 * Aqui morava um índice de 0 a 100 vindo da fixture, com o carimbo "ÍNDICE
 * ILUSTRATIVO" ao lado. O carimbo não impedia nada: três barras de alturas
 * diferentes, numa tela chamada Métricas, orientam pauta mesmo com aviso. O
 * achado F13 chamou isso de série incompleta, e a resposta foi trocar o índice
 * por medição de verdade sobre a população inteira da janela.
 *
 * A idade é fixa de propósito. Comparar séries por acumulado de vida seria
 * comparar quem foi publicado primeiro: o alcance de uma peça sobe com o tempo,
 * e a série mais antiga ganharia sempre.
 *
 * A barra é proporcional à MAIOR média disponível, e não a um teto de 100: não
 * existe teto de alcance, e fingir um faria a mesma medição mudar de altura
 * conforme o teto escolhido. Série sem amostra não desenha barra nenhuma, e diz
 * por quê.
 */
export const SeriesBars = ({series}: {series: readonly SeriesScoreDto[]}) => {
  const headingId = useId();

  const maior = Math.max(
    0,
    ...series.map((entry) =>
      entry.reach.availability === 'available' ? entry.reach.value : 0,
    ),
  );

  const medidas = series.filter(
    (entry) => entry.reach.availability === 'available',
  );

  const description =
    medidas.length > 0
      ? `${medidas[0]!.series.name} tem alcance médio de ${formatMetricValue(
          medidas[0]!.reach,
        )} por peça; ${series.length - medidas.length} de ${series.length} séries sem amostra suficiente`
      : 'Nenhuma série tem amostra suficiente para comparar';

  return (
    <section className="dm-panel" aria-labelledby={headingId}>
      <div className="dm-panel-head">
        <h2 id={headingId}>Desempenho por série</h2>
        <span className="dm-illustrative">ALCANCE MÉDIO POR PEÇA</span>
      </div>
      <div className="dm-bars" role="img" aria-label={description}>
        {series.map((entry) => (
          <div
            className="dm-bar-row"
            key={entry.series.id}
            data-testid="series-bar"
          >
            <span>{entry.series.shortName}</span>
            <div className="dm-track">
              <div
                className="dm-fill"
                style={{
                  width:
                    entry.reach.availability === 'available' && maior > 0
                      ? `${Math.round((entry.reach.value / maior) * 100)}%`
                      : '0%',
                }}
              />
            </div>
            <span className="dm-number">{formatMetricValue(entry.reach)}</span>
          </div>
        ))}
      </div>
      <p className="dm-piece-meta" data-testid="series-sample">
        {series
          .map(
            (entry) =>
              `${entry.series.shortName}: ${describeSourcedMetric(
                entry.reach,
                `${formatSample(entry.reach.availability === 'available' ? entry.reach.sample : null)} peças`,
              )}`,
          )
          .join(' · ')}
      </p>
    </section>
  );
};
